import { useEffect, useState } from 'react'

import type { I18nT } from '@runtime/core/i18n/i18n'
import type { UserServerPayload } from '@runtime/core/store/appStoreTypes'
import { fetchJSON } from '@runtime/shared/api/fetchJSON'

type ResetStage = 'request' | 'confirm' | 'done'

type UseAuthFlowArgs = {
  open: boolean
  locale: string
  t: I18nT
  setAuthToken: (token: string | null) => void
  setUserFromServer: (user?: UserServerPayload | null) => void
  setLoginOpen: (open: boolean) => void
}

type AuthResponse = {
  token?: string | null
  user?: UserServerPayload | null
}

export function useAuthFlow({
  open,
  locale,
  t,
  setAuthToken,
  setUserFromServer,
  setLoginOpen,
}: UseAuthFlowArgs) {
  const [loginEmail, setLoginEmail] = useState('')
  const [loginPassword, setLoginPassword] = useState('')
  const [registerPseudo, setRegisterPseudo] = useState('')
  const [registerEmail, setRegisterEmail] = useState('')
  const [registerPassword, setRegisterPassword] = useState('')
  const [showLoginPassword, setShowLoginPassword] = useState(false)
  const [showRegisterPassword, setShowRegisterPassword] = useState(false)
  const [showReset, setShowReset] = useState(false)
  const [resetStage, setResetStage] = useState<ResetStage>('request')
  const [resetEmail, setResetEmail] = useState('')
  const [resetToken, setResetToken] = useState('')
  const [resetPassword, setResetPassword] = useState('')
  const [showResetPassword, setShowResetPassword] = useState(false)
  const [resetNotice, setResetNotice] = useState('')
  const [resetError, setResetError] = useState('')
  const [resetBusy, setResetBusy] = useState(false)
  const [authError, setAuthError] = useState('')
  const [authBusy, setAuthBusy] = useState(false)

  useEffect(() => {
    if (!open) return

    setAuthError('')
    setAuthBusy(false)
    setLoginEmail('')
    setLoginPassword('')
    setRegisterPseudo('')
    setRegisterEmail('')
    setRegisterPassword('')
    setShowLoginPassword(false)
    setShowRegisterPassword(false)
    setShowReset(false)
    setResetStage('request')
    setResetEmail('')
    setResetToken('')
    setResetPassword('')
    setShowResetPassword(false)
    setResetNotice('')
    setResetError('')
    setResetBusy(false)
  }, [open])

  function mapAuthError(err: unknown) {
    const maybe = err as {
      status?: number
      data?: { message?: string | string[] }
    } | null
    const message = maybe?.data?.message
    const codes: string[] = []
    if (typeof message === 'string') codes.push(message)
    if (Array.isArray(message)) {
      message.forEach((value) => {
        if (typeof value === 'string') codes.push(value)
      })
    }
    const has = (value: string) => codes.includes(value)

    if (has('email_taken')) return t('profile.auth.errors.emailTaken')
    if (has('pseudo_taken')) return t('profile.auth.errors.pseudoTaken')
    if (has('invalid_credentials'))
      return t('profile.auth.errors.invalidCredentials')
    if (has('reset_invalid')) return t('profile.auth.errors.resetInvalid')
    if (has('reset_expired')) return t('profile.auth.errors.resetExpired')
    if (has('auth_email_queue_full') || has('auth_email_unavailable'))
      return t('profile.auth.errors.emailUnavailable')
    if (has('reset_password_length'))
      return t('profile.auth.errors.resetPasswordLength')
    if (maybe?.status === 401)
      return t('profile.auth.errors.invalidCredentials')
    return t('profile.auth.errors.generic')
  }

  async function handleLogin() {
    const email = String(loginEmail || '').trim()
    const password = String(loginPassword || '').trim()
    if (!email || !password) {
      setAuthError(t('profile.auth.errors.missingFields'))
      return
    }
    setAuthBusy(true)
    setAuthError('')
    try {
      const res = await fetchJSON<AuthResponse>('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      })
      if (res?.token) setAuthToken(res.token)
      if (res?.user) setUserFromServer(res.user)
      setLoginOpen(false)
    } catch (error: unknown) {
      setAuthError(mapAuthError(error))
    } finally {
      setAuthBusy(false)
    }
  }

  async function handleRegister() {
    const pseudo = String(registerPseudo || '').trim()
    const email = String(registerEmail || '').trim()
    const password = String(registerPassword || '').trim()
    if (!pseudo || !email || !password) {
      setAuthError(t('profile.auth.errors.missingFields'))
      return
    }
    setAuthBusy(true)
    setAuthError('')
    try {
      const res = await fetchJSON<AuthResponse>('/api/auth/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pseudo, email, password, locale }),
      })
      if (res?.token) setAuthToken(res.token)
      if (res?.user) setUserFromServer(res.user)
      setLoginOpen(false)
    } catch (error: unknown) {
      setAuthError(mapAuthError(error))
    } finally {
      setAuthBusy(false)
    }
  }

  function startResetFlow() {
    setAuthError('')
    setShowReset(true)
    setResetStage('request')
    setResetEmail(String(loginEmail || '').trim())
    setResetToken('')
    setResetPassword('')
    setShowResetPassword(false)
    setResetNotice('')
    setResetError('')
    setResetBusy(false)
  }

  function exitResetFlow() {
    setShowReset(false)
    setResetStage('request')
    setResetNotice('')
    setResetError('')
    setResetBusy(false)
  }

  async function handleResetRequest() {
    const email = String(resetEmail || '').trim()
    if (!email) {
      setResetError(t('profile.auth.errors.missingFields'))
      return
    }
    setResetBusy(true)
    setResetError('')
    setResetNotice('')
    try {
      await fetchJSON('/api/auth/forgot', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, locale }),
      })
      setResetNotice(t('profile.auth.resetSent'))
      setResetStage('confirm')
    } catch (error: unknown) {
      setResetError(mapAuthError(error))
    } finally {
      setResetBusy(false)
    }
  }

  async function handleResetConfirm() {
    const email = String(resetEmail || '').trim()
    const token = String(resetToken || '').trim()
    const password = String(resetPassword || '').trim()
    if (!email || !token || !password) {
      setResetError(t('profile.auth.errors.missingFields'))
      return
    }
    if (password.length < 6) {
      setResetError(t('profile.auth.errors.resetPasswordLength'))
      return
    }
    setResetBusy(true)
    setResetError('')
    setResetNotice('')
    try {
      await fetchJSON('/api/auth/reset', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, token, password }),
      })
      setResetNotice(t('profile.auth.resetUpdated'))
      setResetStage('done')
      setLoginPassword('')
    } catch (error: unknown) {
      setResetError(mapAuthError(error))
    } finally {
      setResetBusy(false)
    }
  }

  return {
    authError,
    authBusy,
    loginEmail,
    loginPassword,
    registerPseudo,
    registerEmail,
    registerPassword,
    showLoginPassword,
    showRegisterPassword,
    showReset,
    resetStage,
    resetEmail,
    resetToken,
    resetPassword,
    showResetPassword,
    resetNotice,
    resetError,
    resetBusy,
    setLoginEmail,
    setLoginPassword,
    setRegisterPseudo,
    setRegisterEmail,
    setRegisterPassword,
    setResetEmail,
    setResetToken,
    setResetPassword,
    toggleLoginPassword: () => setShowLoginPassword((prev) => !prev),
    toggleRegisterPassword: () => setShowRegisterPassword((prev) => !prev),
    toggleResetPassword: () => setShowResetPassword((prev) => !prev),
    handleLogin,
    handleRegister,
    startResetFlow,
    exitResetFlow,
    handleResetRequest,
    handleResetConfirm,
  }
}
